/**
 * What the Vorgang's mails ask for, in the words the screens show it
 * (`CaseDetailOut.mailRequest`, Task 13e; UX-Zweitgutachten 23.10.2026,
 * Nr. 8/9). Read by rule on the server, never by a model; a wish, not an
 * assignment (A-014) — nothing here changes the Vorgang.
 *
 * In `lib/` and not under one feature: the planning's head and the Eingang
 * both say it, and features do not import each other (R-W1).
 */
import type { CaseDetailOut } from "@/api";

import { gratingTypeLabel } from "./gratingTypes";

export type MailRequest = NonNullable<CaseDetailOut["mailRequest"]>;

/** A welded grating — a type the Werkbank neither plans nor prices. */
export const WELDED_NOT_PRICED = "Die Mail nennt einen geschweißten Rost — den rechnet die Werkbank nicht.";

/** „RAL 7016“ · „RAL 7016 und RAL 9005“ · „A, B und C“. */
function listed(names: readonly string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} und ${names.at(-1)}`;
}

/** The coating the mails name, as a sentence for the planning — or `null` where they name none. */
export function coatingSentence(request: MailRequest): string | null {
  if (request.ralNumbers.length > 0) {
    return `Die Mail nennt ${listed(request.ralNumbers)} — Beschichtung wird nicht kalkuliert.`;
  }
  return request.coatingNamed ? "Die Mail nennt eine Beschichtung — sie wird nicht kalkuliert." : null;
}

/** The same as a value beside the other things read from the mail (Eingang) — or `null`. */
export function coatingValue(request: MailRequest): string | null {
  if (request.ralNumbers.length > 0) return listed(request.ralNumbers);
  return request.coatingNamed ? "genannt, ohne RAL-Nummer" : null;
}

/** What a Vorgang without a type is priced as — the server's default (§15.14). */
const PRICED_WITHOUT_TYPE = "pressrost";

/**
 * What the mails asked for and the offer does not hold, for the question
 * before „Angebot raus" (UX-Gutachten 4) — a release given before the mail
 * checks went out with no word. Said, never locked: the person may send what
 * the mail did not ask for. Against the Vorgang's type, as the planning's
 * head compares; empty where nothing was read or nothing departs.
 */
export function sendWarnings(request: MailRequest | null | undefined, gratingType: string | null | undefined): string[] {
  if (!request) return [];
  const warnings: string[] = [];
  // A Vorgang from before §15.14 carries no type and is priced as Pressrost
  // (`pricing.default_params`) — compared as the planning compares it.
  const offered = gratingType ?? PRICED_WITHOUT_TYPE;
  if (request.gratingType && request.gratingType !== offered) {
    warnings.push(
      `Die Mail nennt ${gratingTypeLabel(request.gratingType)} — angeboten ist ${gratingTypeLabel(offered)}.`,
    );
  }
  if (request.weldedNamed) warnings.push("Die Mail nennt einen geschweißten Rost — er ist nicht enthalten.");
  if (request.ralNumbers.length > 0) {
    warnings.push(`Die Mail nennt ${listed(request.ralNumbers)} — eine Beschichtung ist nicht enthalten.`);
  } else if (request.coatingNamed) {
    warnings.push("Die Mail nennt eine Beschichtung — sie ist nicht enthalten.");
  }
  return warnings;
}
